// React and dispatch helpers for the automation surface.
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import paramChannel from '~/lib/paramChannel'
import { getObjectInitial, parseLivePath, resolve, schemaForLivePath } from '~/lib/params'
import store from '~/store'
import { linkModulator, setModulator, updateModulator } from '~/store/live'
import { drafted, draftLoop, takeDraft, useDraft } from './drafts'
import { getEngine, onAutomationSample, sampleOf } from './index'
import { baseValue } from './engine'
import { phaseAtPosition, positionOfValue } from './evaluate'
import { canLoop, defOf, isDriven, linksOf, newLoop, splitDef } from './model'

/** The loop on `path` (a shared drive's resolved) with any gesture's drafts over it, or undefined. */
export const useLoop = path => {
	const own = useSelector(s => (path ? s.live.modulators?.[path] : undefined))
	const driver = useSelector(s => (own?.src ? s.live.modulators?.[own.src] : undefined))
	const draft = useDraft(path)
	const shared = useDraft(own?.src)
	return useMemo(() => {
		const { modulators, objects } = store.getState().live
		return drafted(path, modulators, objects)
	}, [path, own, driver, draft, shared])
}

/** How many other params share `path`'s drive. */
export const useLinkCount = path =>
	useSelector(s => {
		const src = s.live.modulators?.[path]?.src
		return src ? linksOf(s.live.modulators, src).length - 1 : 0
	})

/** The loop on `path` while it drives the param; undefined on `manual` or none. */
export const useDriven = path => {
	const loop = useLoop(path)
	return isDriven(loop) ? loop : undefined
}

/** Can `path` take a loop in the current scene? */
export const useCanLoop = path => useSelector(s => !!path && canLoop(path, s.live.objects))

export { useLoopSample } from './useLoopSample'

/** Has the playing one-shot on `path` reached its end? Re-renders only when that flips. */
export const useLoopEnded = (path, looped) => {
	const [ended, setEnded] = useState(false)
	useEffect(() => {
		if (!looped) return setEnded(false)
		return onAutomationSample(() => setEnded(!!getEngine()?.ended(path)))
	}, [path, looped])
	return ended
}

/** Play an ended one-shot again from its start. */
export const replayLoop = path => getEngine()?.replay(path)

/** What the param shows now: its sample while looped, else its own number. */
export const currentValue = path => {
	const sample = sampleOf(path)
	if (sample !== undefined) return sample
	// A drag or a fade holds an effect param's live value in the channel until it settles.
	if (paramChannel.has(path)) return resolve(paramChannel.get(path), schemaForLivePath(path))
	return baseValue(store.getState().live, path)
}

/** Where the loop body's playhead sits: the loop's position while looped, else the param's own number. */
export const loopPlayhead = path => getEngine()?.playhead(path, performance.now()) ?? currentValue(path)

/** Where `def` puts its playhead on the param's current value — a timeline's phase, an audio drive's rest — so a drive taking over moves nothing. */
const playheadHere = (path, def) => {
	const p = positionOfValue(def, currentValue(path))
	return def.drive === 'fft' ? { rest: p } : { phase: phaseAtPosition(def, p) }
}

export const startLoop = (path, schema, patch) => {
	const def = { ...newLoop(schema, undefined, currentValue(path)), ...patch }
	store.dispatch(setModulator({ path, def: { ...playheadHere(path, def), ...def } }))
}

/** Put the drive on `path` back to `newLoop`'s, its playhead on the current value: one undo step that moves nothing. */
export const resetDrive = (path, schema) => {
	const loop = defOf(store.getState().live.modulators, path)
	if (!isDriven(loop)) return
	const def = newLoop(schema, loop.drive, currentValue(path))
	store.dispatch(setModulator({ path, def: { ...def, ...playheadHere(path, def) } }))
	getEngine()?.restart(path)
}

export const editLoop = (path, patch) => store.dispatch(updateModulator({ path, patch }))

/** `write(patch, transient)` for a surface editing the loop on `path`, creating it if absent. Transient frames only draft (`drafts.js`); the gesture's final write puts the whole draft in Redux, one undo step. */
export const useLoopWriter = (path, schema) => {
	const exists = useSelector(s => !!s.live.modulators?.[path])
	const src = useSelector(s => s.live.modulators?.[path]?.src)
	return useCallback(
		(patch, transient) => {
			if (transient) {
				if (!exists) return
				// A shared drive's motion drafts on its driver, so every param it drives moves with the gesture.
				const { motion, mapping } = src ? splitDef(patch) : { mapping: patch }
				if (motion && Object.keys(motion).length) draftLoop(src, motion)
				if (Object.keys(mapping).length) draftLoop(path, mapping)
				return
			}
			const full = { ...(src && takeDraft(src)), ...takeDraft(path), ...patch }
			if (exists) editLoop(path, full)
			else startLoop(path, schema, full)
		},
		[exists, src, path, schema]
	)
}

const isTimeline = def => def?.drive === 'time' || def?.drive === 'bpm'

/** Pick what drives `path`; a new drive arrives where the value is, moving nothing (beats ↔ seconds keeps running). */
export const setDrive = (path, schema, drive) => {
	const loop = defOf(store.getState().live.modulators, path)
	if (loop?.drive === drive) return
	if (drive === 'manual') {
		if (loop) store.dispatch(updateModulator({ path, patch: { drive }, value: currentValue(path) }))
		return
	}
	if (!loop) return startLoop(path, schema, { drive })
	const next = { ...loop, drive }
	const patch = isTimeline(loop) && isTimeline(next) ? { drive } : { drive, run: false, ...playheadHere(path, next) }
	store.dispatch(updateModulator({ path, patch }))
}

/** The Timeline drive `setDrive` would put on `path`, unstored: what an opened body shows until its first edit. */
export const previewLoop = (path, schema) => {
	const loop = defOf(store.getState().live.modulators, path)
	const def = loop ? { ...loop, drive: 'time', run: false } : newLoop(schema, 'time', currentValue(path))
	return { ...def, ...playheadHere(path, def) }
}

/** Turn on the previewed drive with the edit that turned it on: one undo step. */
export const commitPreview = (path, schema, patch) => {
	const loop = defOf(store.getState().live.modulators, path)
	if (isDriven(loop)) return patch && editLoop(path, patch)
	if (!loop) return startLoop(path, schema, { drive: 'time', ...patch })
	const patched = { drive: 'time', run: false, ...patch }
	store.dispatch(updateModulator({ path, patch: { ...playheadHere(path, { ...loop, ...patched }), ...patched } }))
}

/** The param's own control on a looped param: a running loop is held until the final write releases it (plan A2); a paused one parks where its output is `value`. */
export const touchLoop = (path, value, transient) => {
	const def = defOf(store.getState().live.modulators, path)
	// `seekLoop` takes a playhead along in→out; through an envelope that is not the value itself.
	const parked = def?.envelope && !def.run && def.drive !== 'fft'
	seekLoop(path, parked ? def.in + (def.out - def.in) * positionOfValue(def, value) : value)
	if (!transient) releaseLoop(path)
}

/** Double-click reset on a looped param: a touch to its initial, never a write to the inert number. */
export const resetLoop = path => {
	const p = parseLivePath(path)
	const { objects } = store.getState().live
	const obj = p?.scope === 'object' ? objects?.[p.id] : undefined
	const initial = obj ? getObjectInitial(obj.type, p.key) : schemaForLivePath(path, objects)?.initial
	if (initial !== undefined) touchLoop(path, initial)
}

/** The loop body's playhead: moves a paused loop; touches a running one, or an audio drive (which never pauses), until `releaseLoop`. */
export const seekLoop = (path, value) => {
	const engine = getEngine()
	if (!engine) return
	const def = defOf(store.getState().live.modulators, path)
	if (def?.run || def?.drive === 'fft') engine.touch(path, value)
	else engine.seek(path, value)
}

export const releaseLoop = path => getEngine()?.release(path, performance.now())

/** The loop body's playhead. The body exists only while driven; a drive switched off mid-drag leaves the rest of the drag inert. */
export const scrubParam = (path, value) => {
	if (isDriven(defOf(store.getState().live.modulators, path))) seekLoop(path, value)
}

export const endScrub = releaseLoop

/** Drive `path` the way `from` is driven — one drive, edited from any param it drives. `path` keeps its own in/out if it had a drive, else takes its whole range. */
export const linkDrive = (path, schema, from) => {
	const own = defOf(store.getState().live.modulators, path) ?? newLoop(schema, undefined, currentValue(path))
	store.dispatch(linkModulator({ path, from, mapping: splitDef(own).mapping }))
}

/** Take `path` off its shared drive, keeping a copy of it as its own. */
export const unlinkDrive = path => store.dispatch(linkModulator({ path, from: null }))
